/** 관제 구역(risk_detection_zones) → 타일·크롭 유틸 */

import type { CSSProperties } from 'react';

export type ZoneRect = [number, number, number, number];

export type MonitoringZoneTile<T> = {
  cctv: T;
  zoneIndex: number;
  viewCrop: ZoneRect;
};

export function normalizeZoneRect(
  x0: number,
  y0: number,
  x1: number,
  y1: number
): ZoneRect {
  return [
    Math.min(x0, x1),
    Math.min(y0, y1),
    Math.max(x0, x1),
    Math.max(y0, y1),
  ];
}

/** 드래그로 그릴 때 영상(화면) 가로세로 비율 유지 */
export function aspectLockedZoneRect(
  startX: number,
  startY: number,
  curX: number,
  curY: number,
  mediaW: number,
  mediaH: number
): ZoneRect {
  const mw = Math.max(1, mediaW);
  const mh = Math.max(1, mediaH);
  const aspect = mw / mh;
  const signX = curX < startX ? -1 : 1;
  const signY = curY < startY ? -1 : 1;
  let w = Math.abs(curX - startX);
  let h = Math.abs(curY - startY);
  if (w < 1 && h < 1) {
    return normalizeZoneRect(startX, startY, startX, startY);
  }
  if (w / aspect >= h) {
    h = w / aspect;
  } else {
    w = h * aspect;
  }

  const maxW = signX > 0 ? mw - startX : startX;
  const maxH = signY > 0 ? mh - startY : startY;
  const scale = Math.min(
    maxW > 0 ? maxW / Math.max(w, 1e-6) : 0,
    maxH > 0 ? maxH / Math.max(h, 1e-6) : 0,
    1
  );
  w *= Math.max(0, scale);
  h = w / aspect;

  const x2 = startX + signX * w;
  const y2 = startY + signY * h;
  return normalizeZoneRect(startX, startY, x2, y2);
}

export function listValidRiskZones(
  zones: number[][] | null | undefined
): ZoneRect[] {
  if (!Array.isArray(zones)) return [];
  const out: ZoneRect[] = [];
  for (const z of zones) {
    if (!Array.isArray(z) || z.length < 4) continue;
    const rect = normalizeZoneRect(
      Number(z[0]),
      Number(z[1]),
      Number(z[2]),
      Number(z[3])
    );
    if (rect[2] - rect[0] < 2 || rect[3] - rect[1] < 2) continue;
    out.push(rect);
  }
  return out;
}

/** 구역이 있는 CCTV만, 구역마다 타일 1개 */
export function listMonitoringZoneTiles<
  T extends { risk_detection_zones?: number[][] | null },
>(cctvs: T[]): MonitoringZoneTile<T>[] {
  const out: MonitoringZoneTile<T>[] = [];
  for (const cctv of cctvs) {
    const zones = listValidRiskZones(cctv.risk_detection_zones);
    zones.forEach((viewCrop, zoneIndex) => {
      out.push({ cctv, zoneIndex, viewCrop });
    });
  }
  return out;
}

/** 풀프레임 bbox → 크롭 좌표계. 구역 밖은 제외 */
export function remapBboxToCrop(
  bbox: number[],
  crop: ZoneRect
): { bbox: ZoneRect; frameW: number; frameH: number } | null {
  if (!Array.isArray(bbox) || bbox.length < 4) return null;
  const [zx1, zy1, zx2, zy2] = crop;
  const zw = Math.max(1, zx2 - zx1);
  const zh = Math.max(1, zy2 - zy1);
  const x1 = Number(bbox[0]);
  const y1 = Number(bbox[1]);
  const x2 = Number(bbox[2]);
  const y2 = Number(bbox[3]);
  if (x2 < zx1 || x1 > zx2 || y2 < zy1 || y1 > zy2) return null;
  return {
    bbox: [
      Math.max(0, x1 - zx1),
      Math.max(0, y1 - zy1),
      Math.min(zw, x2 - zx1),
      Math.min(zh, y2 - zy1),
    ],
    frameW: zw,
    frameH: zh,
  };
}

/** CSS: 타일 안에서 crop 영역만 보이도록 풀프레임 미디어를 배치 */
export function zoneCropFrameStyle(
  crop: ZoneRect,
  mediaW: number,
  mediaH: number
): CSSProperties {
  const mw = Math.max(1, mediaW);
  const mh = Math.max(1, mediaH);
  const [x1, y1, x2, y2] = crop;
  const zw = Math.max(1, x2 - x1);
  const zh = Math.max(1, y2 - y1);
  return {
    position: 'absolute',
    width: `${(mw / zw) * 100}%`,
    height: `${(mh / zh) * 100}%`,
    left: `${(-x1 / zw) * 100}%`,
    top: `${(-y1 / zh) * 100}%`,
    maxWidth: 'none',
    objectFit: 'fill',
  };
}
